import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import './Routes.css';
import { createRoute, updateRoute, fetchRoutes, deleteRoute } from '../../services/routeService';
import { useAuth } from '../../contexts/AuthContext';
import RoutesList from './components/RoutesList';
import RouteCreation from './components/RouteCreation';
import RouteViewer from './components/RouteViewer';
import RouteView from './components/RouteView';
import RouteEditor from './components/RouteEditor';
import supabase from '../../services/supabase';
import { getMapboxToken } from '../../services/mapService';

// Set Mapbox token consistently and disable telemetry
try {
  const token = getMapboxToken();
  if (token) {
    mapboxgl.accessToken = token;
  } else {
    // eslint-disable-next-line no-console
    console.error('[Routes] Missing Mapbox token; route editor map disabled');
  }
  if (typeof window !== 'undefined') {
    window.MAPBOX_DISABLE_TELEMETRY = true;
  }
} catch (_) {}

// Accept isOwnProfile and userId as props, plus all the state management
const Routes = ({ 
  routes: initialRoutes = [], 
  isOwnProfile, 
  userId,
  isCreating,
  setIsCreating,
  selectedRoute,
  setSelectedRoute,
  selectedRoutePoints,
  setSelectedRoutePoints,
  loadingSelectedRoute,
  setLoadingSelectedRoute,
  isEditing,
  setIsEditing
}) => {
  // --- All hooks at the top ---
  const mapContainerRef = useRef(null);
  const [map, setMap] = useState(null);
  const [routes, setRoutes] = useState(initialRoutes || []);
  const [editingRouteId, setEditingRouteId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', description: '' });
  // Add isMapReady state
  const [isMapReady, setIsMapReady] = useState(false);
  const mapRef = useRef(null);

  // Debug effect to track when Routes component mounts
  useEffect(() => {
    console.log('[DEBUG] Routes component mounted/updated:', {
      routes: routes.length,
      isCreating,
      selectedRoute: selectedRoute?.name || 'undefined',
      userId,
      timestamp: new Date().toISOString()
    });
    
    // Debug container dimensions
    if (mapContainerRef.current) {
      const rect = mapContainerRef.current.getBoundingClientRect();
      console.log('[DEBUG] Container dimensions on mount:', {
        width: rect.width,
        height: rect.height,
        top: rect.top,
        left: rect.left,
        right: rect.right,
        bottom: rect.bottom
      });
    }
  }, [routes, isCreating, selectedRoute, userId]);

  // Debug effect to track when selectedRoute changes
  useEffect(() => {
    console.log('[DEBUG] selectedRoute changed:', {
      selectedRoute: selectedRoute?.name || 'undefined',
      selectedRouteId: selectedRoute?.id || 'undefined',
      timestamp: new Date().toISOString()
    });
  }, [selectedRoute]);

  // Debug effect to track when selectedRoutePoints changes
  useEffect(() => {
    console.log('[DEBUG] selectedRoutePoints changed:', {
      pointsCount: selectedRoutePoints?.length || 0,
      loadingSelectedRoute,
      timestamp: new Date().toISOString()
    });
    
    // Detailed point analysis
    if (selectedRoutePoints && selectedRoutePoints.length > 0) {
      console.log('[DEBUG] === DETAILED POINT ANALYSIS ===');
      console.log('[DEBUG] Total points:', selectedRoutePoints.length);
      
      selectedRoutePoints.forEach((point, index) => {
        console.log(`[DEBUG] Point ${index + 1}:`, {
          id: point.id,
          name: point.name,
          location_search: point.location_search,
          latitude: point.latitude,
          longitude: point.longitude,
          coordinates: point.coordinates,
          hasCoordinates: !!point.coordinates,
          coordinatesType: Array.isArray(point.coordinates) ? 'array' : typeof point.coordinates,
          coordinatesLength: Array.isArray(point.coordinates) ? point.coordinates.length : 'N/A',
          point_order: point.point_order,
          tags: point.tags,
          notes: point.notes
        });
        
        // Validate coordinates
        if (point.coordinates && Array.isArray(point.coordinates)) {
          const [lng, lat] = point.coordinates;
          console.log(`[DEBUG] Point ${index + 1} coordinate validation:`, {
            lng: lng,
            lat: lat,
            lngType: typeof lng,
            latType: typeof lat,
            lngValid: typeof lng === 'number' && !isNaN(lng),
            latValid: typeof lat === 'number' && !isNaN(lat),
            bothValid: typeof lng === 'number' && typeof lat === 'number' && !isNaN(lng) && !isNaN(lat)
          });
        } else {
          console.warn(`[WARNING] Point ${index + 1} missing or invalid coordinates array:`, point.coordinates);
        }
      });
      console.log('[DEBUG] === END POINT ANALYSIS ===');
    } else if (selectedRoutePoints && selectedRoutePoints.length === 0) {
      console.log('[DEBUG] ⚠️ Route has 0 points - this explains why no markers appear');
    }
  }, [selectedRoutePoints, loadingSelectedRoute]);

  // Sync routes from parent when they change to avoid overwriting with stale fetches
  useEffect(() => {
    setRoutes(initialRoutes || []);
  }, [initialRoutes]);

  // Fetch routes only if parent did not provide any (prevents empty overwrite on auth race)
  useEffect(() => {
    async function fetchUserRoutes() {
      if (!userId) return;
      if (initialRoutes && initialRoutes.length > 0) return;
      try {
        console.log('[DEBUG] === FETCHING USER ROUTES (fallback) ===');
        console.log('[DEBUG] User ID:', userId);
        const userRoutes = await fetchRoutes(userId);
        console.log('[DEBUG] Raw routes from API:', userRoutes);
        setRoutes(userRoutes || []);
      } catch (err) {
        console.error('[ERROR] Failed to fetch routes:', err);
        setRoutes([]);
      }
    }
    fetchUserRoutes();
  }, [userId, initialRoutes]);


  // --- Map creation effect: runs when selectedRoute or isCreating changes ---
  useEffect(() => {
    console.log('[DEBUG] Map initialization effect triggered');
    
    // Skip if no container or if we're not in a state that needs a map
    if (!mapContainerRef.current || (!selectedRoute && !isCreating)) {
      console.log('[DEBUG] No container or not in map state, skipping initialization');
      return;
    }

    // Clean up any previous map instance
    if (mapRef.current) {
      console.log('[DEBUG] Cleaning up previous map instance');
      mapRef.current.remove();
      mapRef.current = null;
    }
    setIsMapReady(false);

    // Check container dimensions
    const container = mapContainerRef.current;
    const rect = container.getBoundingClientRect();
    console.log('[DEBUG] Container dimensions:', {
      width: rect.width,
      height: rect.height,
      visible: rect.width > 0 && rect.height > 0
    });

    // If container has no dimensions, wait a bit and try again
    if (rect.width === 0 || rect.height === 0) {
      console.log('[DEBUG] Container has zero dimensions, waiting for render...');
      const timeout = setTimeout(() => {
        // Force re-run of this effect
        console.log('[DEBUG] Retrying map initialization after timeout');
      }, 100);
      return () => clearTimeout(timeout);
    }

    // Determine initial center and zoom
    let initialCenter = [-98.5795, 39.8283]; // Default to center of US
    let initialZoom = 10.2;

    // For route creation mode, use a world view
    if (isCreating && !selectedRoute) {
      initialCenter = [0, 0]; // World center
      initialZoom = 1; // Maximum zoom out to see the whole world
      console.log('[DEBUG] Route creation mode - using world view:', initialCenter, 'zoom:', initialZoom);
    }
    // If points are loaded and not loading, center on last point with coordinates
    else if (!loadingSelectedRoute && selectedRoutePoints && selectedRoutePoints.length > 0) {
      const lastPointWithCoords = selectedRoutePoints.slice().reverse().find(p => p.coordinates && Array.isArray(p.coordinates) && p.coordinates.length === 2);
      if (lastPointWithCoords) {
        initialCenter = lastPointWithCoords.coordinates;
        initialZoom = 15;
        console.log('[DEBUG] Using point-based center:', initialCenter);
      }
    }

    console.log('[DEBUG] Initializing map with center:', initialCenter, 'zoom:', initialZoom);

    // Use the simple pattern from LocalMap.js
    if (!mapContainerRef.current) {
      console.log('[DEBUG] No container, returning');
      return;
    }

    // Clear any existing content
    mapContainerRef.current.innerHTML = '';

    console.log('[DEBUG] Creating Mapbox map instance...');
    const token = getMapboxToken();
    if (!token) {
      console.error('[Routes] No Mapbox token; skipping map creation');
      return;
    }
    const mapInstance = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/streets-v11',
      center: initialCenter,
      zoom: initialZoom,
      minZoom: 1,
      maxZoom: 22,
    });
      
    console.log('[DEBUG] Map instance created, setting ref...');
    mapRef.current = mapInstance;
    console.log('[DEBUG] Map ref set:', !!mapRef.current);
      
    mapInstance.on('load', () => {
      console.log('[DEBUG] Map loaded successfully');
      // Force resize multiple times to ensure proper sizing
      setTimeout(() => {
        if (mapInstance && mapInstance._canvas) {
          console.log('[DEBUG] Resizing map...');
          mapInstance.resize();
          
          // Force another resize after a short delay
          setTimeout(() => {
            if (mapInstance && mapInstance._canvas) {
              console.log('[DEBUG] Second resize...');
              mapInstance.resize();
            }
          }, 200);
        }
      }, 100);
      setIsMapReady(true);
    });

    // Also add a resize listener for window changes
    const handleResize = () => {
      if (mapInstance && mapInstance._canvas) {
        console.log('[DEBUG] Window resize - resizing map');
        mapInstance.resize();
      }
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      console.log('[DEBUG] Cleaning up map...');
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      setIsMapReady(false);
    };

    console.log('[DEBUG] Map initialization completed');
    
    return () => {
      console.log('[DEBUG] Cleaning up map...');
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      // Reset map ready state when map is destroyed
      setIsMapReady(false);
    };
  }, [selectedRoute, isCreating]); // Remove isMapReady dependency to prevent loop

    // --- Cleanup effect: removes any existing custom markers when entering route viewing mode ---
  useEffect(() => {
    if (selectedRoute && mapRef.current) {
      console.log('[DEBUG] Route viewing mode - cleaning up any existing custom markers');
      
      // Remove any existing custom route markers
      const existingMarkers = document.querySelectorAll('.route-marker');
      existingMarkers.forEach(markerEl => {
        const marker = markerEl._mapboxMarker;
        if (marker) marker.remove();
        markerEl.remove();
      });
    }
  }, [selectedRoute]);

  const handleCreateRoute = () => {
    setIsCreating(true);
  };

  const cancelCreation = () => {
    setIsCreating(false);
  };

  // Edit route handlers
  const startEditRoute = (route) => {
    setEditingRouteId(route.id);
    setEditForm({ name: route.name, description: route.description });
  };
  const cancelEditRoute = () => {
    setEditingRouteId(null);
    setEditForm({ name: '', description: '' });
  };
  const handleEditInput = (e) => {
    setEditForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };
  const saveEditRoute = async (routeId) => {
    try {
      await updateRoute(routeId, editForm);
      setRoutes(routes.map(r => r.id === routeId ? { ...r, ...editForm } : r));
      setEditingRouteId(null);
      setEditForm({ name: '', description: '' });
      alert('Route updated!');
    } catch (err) {
      alert('Failed to update route.');
    }
  };



  // --- Render routes list in left panel ---
  console.log('[DEBUG] 🎭 Routes rendering logic:', {
    isCreating,
    selectedRoute: selectedRoute?.name || 'undefined',
    selectedRouteId: selectedRoute?.id || 'undefined',
    shouldShowRoutesList: !isCreating && !selectedRoute,
    shouldShowRouteViewer: !!selectedRoute,
    shouldShowRouteCreation: isCreating,
    timestamp: new Date().toISOString()
  });

  if (!isCreating && !selectedRoute) {
    console.log('[DEBUG] 📋 Rendering RoutesList component');
    const handleEditRouteFromList = (route) => {
      if (!route) return;
      setSelectedRoute(route);
      setIsEditing(true);
    };
    const handleDeleteRouteFromList = async (routeId) => {
      if (!routeId) return;
      const confirmed = window.confirm('Delete this route? This cannot be undone.');
      if (!confirmed) return;
      try {
        await deleteRoute(routeId);
        setRoutes(prev => (prev || []).filter(r => r.id !== routeId));
        if (selectedRoute && selectedRoute.id === routeId) {
          setSelectedRoute(null);
          setSelectedRoutePoints([]);
          setIsEditing(false);
        }
        alert('Route deleted.');
      } catch (err) {
        console.error('[Routes] Failed to delete route from list', err);
        alert('Failed to delete route.');
      }
    };
    return (
      <RoutesList
        routes={routes}
        setRoutes={setRoutes}
        isOwnProfile={isOwnProfile}
        onCreateRoute={handleCreateRoute}
        onSelectRoute={setSelectedRoute}
        selectedRoute={selectedRoute}
        setSelectedRoute={setSelectedRoute}
        onEditRoute={handleEditRouteFromList}
        onDeleteRoute={handleDeleteRouteFromList}
      />
    );
  }

  // --- NEW: Render selected route details and map ---
  if (selectedRoute) {
    console.log('[DEBUG] 🗺️ Rendering RouteViewer component for route:', selectedRoute.name);
    // --- Edit mode ---
    if (isEditing) {
      const handleSaveEdit = (updatedRoute, updatedPoints) => {
        setIsEditing(false);
        if (updatedRoute) setSelectedRoute(updatedRoute);
        if (updatedPoints) setSelectedRoutePoints(updatedPoints);
      };

      return (
        <RouteCreation
          userId={userId}
          onCancel={() => setIsEditing(false)}
          mapContainerRef={mapContainerRef}
          mapRef={mapRef}
          mode="edit"
          initialRoute={selectedRoute}
          initialPoints={selectedRoutePoints}
          onSaveEdit={handleSaveEdit}
        />
      );
    }
    // --- END: Edit mode ---

    // Route viewing mode
    const handleDeleteSelectedRoute = async () => {
      if (!selectedRoute) return;
      const confirmed = window.confirm('Are you sure you want to delete this route? This cannot be undone.');
      if (!confirmed) return;
      try {
        await deleteRoute(selectedRoute.id);
        // Remove from local list and reset view
        setRoutes(prev => (prev || []).filter(r => r.id !== selectedRoute.id));
        setSelectedRoute(null);
        setSelectedRoutePoints([]);
        setIsEditing(false);
        alert('Route deleted.');
      } catch (err) {
        console.error('[Routes] Failed to delete route', err);
        alert('Failed to delete route. Please try again.');
      }
    };

    return (
      <RouteView
        selectedRoute={selectedRoute}
        selectedRoutePoints={selectedRoutePoints}
        loadingSelectedRoute={loadingSelectedRoute}
        onBack={() => setSelectedRoute(null)}
        onEdit={() => setIsEditing(true)}
        onDelete={handleDeleteSelectedRoute}
        mapContainerRef={mapContainerRef}
        mapRef={mapRef}
        userId={userId}
        canEdit={!!isOwnProfile}
      />
    );
  }

  // Route creation mode
  if (isCreating) {
    console.log('[DEBUG] ➕ Rendering RouteCreation component');
  return (
      <RouteCreation
        userId={userId}
        onCancel={cancelCreation}
        mapContainerRef={mapContainerRef}
        mapRef={mapRef}
        onCreated={(newRoute) => {
          try {
            if (!newRoute) return;
            
            // Add the new route to the routes list
            setRoutes(prevRoutes => [...prevRoutes, newRoute]);
            
            // Return to routes list instead of viewing the created route
            // This allows users to create multiple routes in succession
            setSelectedRoute(null);
            setSelectedRoutePoints([]);
            setIsCreating(false);
            
            // Show success message
            alert(`Route "${newRoute.name}" created successfully!`);
          } catch (e) {
            console.warn('[Routes] onCreated handler error', e);
            setIsCreating(false);
          }
        }}
      />
                    );
  }
                  
  console.log('[DEBUG] ❓ No component to render - this should not happen');
  return null;
};

export default Routes; 
